import React from 'react';
import { useQuery } from '@apollo/client';
import { Typography, Card } from '@otp/ui';
import { formatNumber } from '@otp/utils';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';

import {
  UserOrganisationLevelBreakdownDocument,
  UserOrganisationLevelBreakdownQuery,
} from 'src/graphql/generated/graphql';
import { Routes } from 'src/components/common/router/constants';
import clsx from 'clsx';
import { UsersDark } from '@otp/icons';
import { ReactComponent as McdMascot } from './mcd-decor.svg';
import { useNavigate } from 'react-router-dom';

export const MembersInOrganisation = ({
  className,
}: {
  className?: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data } = useQuery<UserOrganisationLevelBreakdownQuery>(
    UserOrganisationLevelBreakdownDocument,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-and-network',
    },
  );
  const levelBreakdown = data?.userOrganisationLevelBreakdown;
  const totalMembers = (
    Object.values(levelBreakdown || {}) as unknown as number[]
  ).reduce((sum: number, count) => sum + count, 0);

  const otpLevels = [
    {
      id: 'otp-3',
      label: t('my-organisation.manage-otps-v2.otp-levels.Otp3'),
      value: levelBreakdown?.['otp-3'] || 0,
    },
    {
      id: 'otp-2',
      label: t('my-organisation.manage-otps-v2.otp-levels.Otp2'),
      value: levelBreakdown?.['otp-2'] || 0,
    },
    {
      id: 'otp-1',
      label: t('my-organisation.manage-otps-v2.otp-levels.Otp1'),
      value: levelBreakdown?.['otp-1'] || 0,
    },
    {
      id: 'otp-0',
      label: t('my-organisation.manage-otps-v2.otp-levels.Otp0'),
      value: levelBreakdown?.['otp-0'] || 0,
    },
  ];

  return (
    <Card
      className={clsx(
        className,
        'bg-[#4B7BE5] text-white hover:bg-[#4B7BE5] rounded-xl',
      )}
      noBorder
    >
      <Grid container gap={2}>
        {/* Header Section */}
        <Grid item xs={12}>
          <Grid container alignItems="center" gap={2}>
            <UsersDark />
            <Typography variant="body1" fontWeight="medium" fontFamily="Inter">
              {t('my-organisation.manage-otps-v2.members-in-organisation')}
            </Typography>
            <Grid item xs container justifyContent="flex-end">
              <McdMascot />
            </Grid>
          </Grid>
        </Grid>

        {/* Total Members */}
        <Grid item xs={12}>
          <Typography variant="h4" fontWeight="medium" fontFamily="Inter">
            {formatNumber(totalMembers)}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Grid container direction="column" gap={2}>
            {otpLevels.map((level) => (
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                key={level.id}
              >
                <Grid item>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    fontFamily="Inter"
                  >
                    {level.label}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    fontFamily="Inter"
                  >
                    {level.value}
                  </Typography>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* CTA */}
        <Grid item xs={12}>
          <Card
            isLink
            noBorder
            className="bg-black text-white py-3 rounded-xl w-full items-center justify-between group"
            linkIconClassName="text-white mr-2 mt-1.5 group-hover:text-black"
            onClick={() => {
              navigate(Routes.users.path);
            }}
          >
            <Typography
              variant="body1"
              fontWeight="medium"
              className="text-white group-hover:text-black"
              fontFamily="Inter"
            >
              {t('my-organisation.manage-otps-v2.cta')}
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Card>
  );
};
